/* eslint-disable no-restricted-globals */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from "react";
import SiteHeader from "@/components/common/SiteHeader";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { Checkbox } from "@/components/shared/ui/checkbox";
import { Input } from "@/components/shared/ui/input";
import { Button } from "@/components/shared/ui/button";
import { DateTime } from "luxon";
import { getLocalStorageAuth, updateLocalStorageAuth } from "@/lib/utils";
import { UTILITY_REDIRECT_URI, utilityEnv } from "@/lib/utilityEnv";

const scopeOptions = [
  {
    key: "CONSUMPTION",
    label: "Consumption Scope",
    value:
      "FB=1_3_4_5_7_10_13_14_18_32_33_35_37_38_41_44;IntervalDuration=Monthly_3600_900_300;BlockDuration=Monthly_Daily;HistoryLength=63113904;",
    checked: true,
  },
  {
    key: "BILLING",
    label: "Billing Scope",
    value:
      "FB=1_3_6_10_13_14_15_16_28_32_33_35_37_38_41_44;IntervalDuration=Monthly;BlockDuration=Monthly;HistoryLength=63113904;",
    checked: true,
  },
  {
    key: "REALTIME",
    label: "Real-Time Scope",
    value:
      "FB=1_3_4_5_7_13_14_18_32_33_35_37_38_41_44;IntervalDuration=900_300;BlockDuration=Daily;HistoryLength=86400;",
    checked: true,
  },
  {
    key: "RETAIL",
    label: "Retail Customer Scope",
    value: "FB=1_3_13_14_46_47;",
    checked: true,
  },
];

const UtilityScopeSelection = () => {
  const [scopes, setScopes] = useState(scopeOptions);
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const {
    accountid,
    startdate = DateTime.now().toFormat("MM/dd/yyyy"),
    enddate = "12/31/2099",
    DataCustodianID = "ConEdison",
    isTest = false,
  } = useSearch({
    from: "/external/utility_scope_selection",
  });
  const currentEnv = utilityEnv();
  const clientIdsByEnv = {
    dev: "0oa21l77rimuWAp9Z0h8",
    prod: "0oa14dlmdcwA0BAju0x8",
  };
  const clientId = clientIdsByEnv[currentEnv];
  const utilityCode =
    DataCustodianID === "ORU" ? "orange_and_rockland_utilities" : "con_ed_ny";

  const baseUrls = {
    ORU: {
      dev: "https://uat10.oru.com/en/",
      prod: "https://www.oru.com/en/",
    },
    ConEdison: {
      dev: "https://uat10.coned.com/en/",
      prod: "https://www.coned.com/en/",
    },
  };

  const bulkIds = {
    dev: "91",
    prod: "48",
  };
  const bulkId = bulkIds[currentEnv];
  const baseUrl = baseUrls[DataCustodianID][currentEnv];

  const checkedScopes = scopes
    .filter((scope) => scope.checked)
    .map((scope) => scope.value)
    .join("|");
  const redirectUri = UTILITY_REDIRECT_URI[currentEnv];

  const url =
    `${baseUrl}oauth/authorize?scope=FB=1_3_4_5_7_10_15_16_51_53_56_57_58_60;` +
    "IntervalDuration=Monthly_3600_900_300;BlockDuration=Monthly_Daily;HistoryLength=63072000;" +
    `BR=${bulkId}&response_type=code&client_id=${clientId}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}&state=<state>`;
  const formattedStartDate = DateTime.fromFormat(
    startdate,
    "MM/dd/yyyy",
  ).toFormat("MM/dd/yyyy");
  const formattedEndDate = DateTime.fromFormat(enddate, "MM/dd/yyyy").toFormat(
    "MM/dd/yyyy",
  );
  const testRedirectUri = `${url}?client_id=${clientId}&scope=${encodeURIComponent(checkedScopes)}&redirectUri=${encodeURIComponent(redirectUri)}&MAID=${accountid}&startDate=${encodeURIComponent(formattedStartDate)}&endDate=${encodeURIComponent(formattedEndDate)}&response_type=code`;

  const handleCheckboxChange = (index: number) => {
    const newScopes = scopes.map((scope, i) => {
      if (i === index) {
        return { ...scope, checked: !scope.checked };
      }
      return scope;
    });
    setScopes(newScopes);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const currentAuth = getLocalStorageAuth("coned", utilityCode);
  const hasBuildingId = !!currentAuth?.buildingId;
  // https://app.getcarbonclear.com/external/utility_scope_selection?accountid=000147232964949&startdate=05/14/2024&enddate=12/31/2099&DataCustodianID=ConEdison
  // https://www.coned.com/en/accounts-billing/dashboard/billing-and-usage/share-my-data-connections/third-party-authorization/redirect?client_id=0oa21l77rimuWAp9Z0h8&scope=FB%3D1_3_4_5_7_10_13_14_18_32_33_35_37_38_41_44%3BIntervalDuration%3DMonthly_3600_900_300%3BBlockDuration%3DMonthly_Daily%3BHistoryLength%3D63113904%3B%7CFB%3D1_3_6_10_13_14_15_16_28_32_33_35_37_38_41_44%3BIntervalDuration%3DMonthly%3BBlockDuration%3DMonthly%3BHistoryLength%3D63113904%3B%7CFB%3D1_3_4_5_7_13_14_18_32_33_35_37_38_41_44%3BIntervalDuration%3D900_300%3BBlockDuration%3DDaily%3BHistoryLength%3D86400%3B%7CFB%3D1_3_13_14_46_47%3B&redirectUri=https%3A%2F%2Fapp.getcarbonclear.com%2Fexternal%2Fauth_success&MAID=000147232964949&startDate=05%2F14%2F2024&endDate=12%2F31%2F2099&response_type=code
  // `${baseUrl}?client_id=${clientId}&scope=${encodeURIComponent(checkedScopes)}&redirectUri=${encodeURIComponent(redirectUri)}&MAID=${accountid}&startDate=${encodeURIComponent(startdate)}&endDate=${encodeURIComponent(enddate)}&response_type=code`;
  const handleSubmit = () => {
    // Check scopes
    if (!scopes.filter((scope) => scope.checked).length) {
      setErrorMessage("Please select at least one scope");
      return;
    }
    setErrorMessage("");
    // Check external auth object - either update it or set it
    if (!hasBuildingId) {
      updateLocalStorageAuth("coned", utilityCode, {
        connType: "coned",
        utilityCode,
        buildingId: null,
        status: "SCOPE_SELECTED",
        email,
        started_on: DateTime.now().toISOTime(),
        updated_on: DateTime.now().toISOTime(),
      });
    } else {
      updateLocalStorageAuth("coned", utilityCode, {
        updated_on: DateTime.now().toISOTime(),
        status: "SCOPE_SELECTED",
      });
    }
    if (!isTest && currentEnv === "prod") {
      window.location.href = testRedirectUri;
    } else {
      window.location.href = testRedirectUri;
      // window.location.href = "/external/auth_success?isTest=1";
    }
  };

  return (
    <div className="flex flex-col w-full">
      <SiteHeader header="Data Authorization Scope" />
      <div className="px-10 py-8 max-w-[600px]">
        <div>Select the types of data authorization: </div>
        <div>
          Note: Full access is recommended for the most complete
          recommendations, and may be required for some reports. Reach out to
          support@getcodeclear.com with any questions.
        </div>
        <div className="my-4 flex flex-col gap-2">
          {scopes.map((scope, index) => (
            <div key={scope.key} className="items-top flex space-x-2">
              <Checkbox
                id={scope.key}
                checked={scope.checked}
                onCheckedChange={() => handleCheckboxChange(index)}
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor={scope.key}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {scope.label}
                </label>
              </div>
            </div>
          ))}
        </div>
        {!hasBuildingId && (
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email:
            </label>
            <Input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={handleEmailChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
        )}
        <div className="mt-6">
          <Button onClick={handleSubmit}>Complete Authorization</Button>
          {errorMessage && <div className="text-red-500">{errorMessage}</div>}
        </div>
      </div>
    </div>
  );
};

type UtilityScopeSelectionSearch = {
  accountid?: string;
  startdate?: string;
  enddate?: string;
  DataCustodianID?: string;
  isTest?: boolean;
};
export const Route = createFileRoute("/external/utility_scope_selection")({
  component: UtilityScopeSelection,
  validateSearch: (
    search: Record<string, string>,
  ): UtilityScopeSelectionSearch => ({
    accountid: search?.accountid || undefined,
    startdate: search?.startdate || undefined,
    enddate: search?.enddate || undefined,
    DataCustodianID: search?.DataCustodianID || undefined,
    isTest:
      typeof search?.isTest !== "undefined"
        ? Boolean(search?.isTest)
        : undefined,
  }),
});
